import { useEffect, useMemo, useState } from 'react'
import type { ApolloClient } from '@apollo/client'
import { createWebClient } from '@/services/ApolloClient'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { GuildSale, getGuildSale } from './SaleService'

interface UseGuildSaleResult {
  loading: boolean
  sale: GuildSale | null
}

export function useGuildSale(): UseGuildSaleResult {
  const { locale } = useLocale()
  const [sale, setSale] = useState<GuildSale | null>(null)
  const [loading, setLoading] = useState(true)

  const federationClient = useMemo(() => {
    return createWebClient({ locale })
  }, [locale])

  useEffect(() => {
    let isMounted = true

    const fetchSale = async (client: ApolloClient<object>) => {
      setLoading(true)
      try {
        const guildSale = await getGuildSale({
          opts: { locale, preview: false },
          federationClient: client,
        })
        if (isMounted) {
          setSale(guildSale)
        }
      } catch (error) {
        logger().error('Failed to load guild sale in useGuildSale', { locale }, error)
        if (isMounted) {
          setSale(null)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchSale(federationClient)

    return () => {
      isMounted = false
    }
  }, [federationClient, locale])

  return { loading, sale }
}
